import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGroups from '@/hooks/contacts/use-groups';
import { useAuthStore } from '@/stores/auth';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { addToast } from '@/stores/toast';
import { getAllSessions } from '@/utils/cache-session';
import { generateSessionKey } from '@/utils/session';
import { useIntercomDispatcher } from '../comm/use-comm';
import useUserPlatform from '../use-user-platform';

export default function useClearAllDeviceSession() {
  const { hideAllCurrentSessions } = useConversationSessionStore();
  const { groups } = useGroups();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { dispatchAsync, dispatch } = useIntercomDispatcher();
  const { isEmbedded } = useUserPlatform();
  const { auth } = useAuthStore();

  const clearAllDeviceSession = async () => {
    try {
      setIsLoading(true);
      for (const session of getAllSessions()) {
        usePeriodicMessageStore
          .getState()
          .setPurgedMessageId(generateSessionKey(session.SessionType, session.ID), session.LastMsgID);
        await useMessageStore.getState().hideAll(session.SessionType, session.ID);
        if (session.SessionType === 'group') {
          const group = groups[session.ID];
          if (group?.Info) {
            useConversationSessionStore
              .getState()
              .setLastReadNotice(generateSessionKey('group', session.ID), group.Info);
          }
        }
      }
      hideAllCurrentSessions();
      if (isEmbedded && auth?.UserID) {
        await dispatchAsync({ event: 'clear-all-device-session', data: { userId: auth?.UserID } }, [
          'clear-all-device-session-success',
          'clear-all-device-session-failed',
        ]);
      } else {
        addToast({
          type: 'success',
          message: t('messaging-settings.success'),
        });
        if (auth?.UserID) {
          dispatch({
            event: 'clear-all-device-session',
            data: { userId: auth?.UserID },
          });
        }
      }
    } catch (error: unknown) {
      addToast({
        type: 'error',
        message: t('messaging-settings.failed'),
      });
      setError(error as string);
    } finally {
      setIsLoading(false);
    }
  };

  return { clearAllDeviceSession, isLoading, error };
}
